import { DISTRICT, DISTRICTS_PER_ERA, type DistrictKind } from '@somnhold/content';
import { DIRS, hexDist, hexLen, type Hex } from './hex.ts';
import { idiv } from './math/fixed.ts';
import type { MapDef, SectorDef } from './types.ts';

/**
 * The walled ground of a bastion: the disc inside the ring plus its districts (docs/ERAS.md). The wall
 * runs along every cell just outside it; entrances are where the paths meet that wall.
 */
export interface DistrictDef {
  side: number;
  kind: DistrictKind;
}

/** Centre of the district on a side of the bastion: past the ring, on the middle of that side. */
export const districtCenter = (bastion: number, side: number): Hex => {
  const a = DIRS[side % 6] as Hex;
  const b = DIRS[(side + 1) % 6] as Hex;
  const n = bastion + DISTRICT.reach;
  const ha = n - (n >> 1);
  const hb = n >> 1;
  return { q: a.q * ha + b.q * hb, r: a.r * ha + b.r * hb };
};

const INSIDE = new WeakMap<number[], Uint8Array>();
/** A wave's terraced land: whether a cell is inside the walls (the keep's is). */
const landInside = (map: MapDef, q: number, r: number): boolean => {
  const R = map.radius;
  if (q === 0 && r === 0) return true;
  const list = map.land?.inside;
  if (!list || hexLen(q, r) > R) return false;
  let mask = INSIDE.get(list);
  if (!mask) {
    mask = new Uint8Array((2 * R + 1) * (2 * R + 1));
    for (const id of list) mask[id] = 1;
    INSIDE.set(list, mask);
  }
  return mask[(r + R) * (2 * R + 1) + q + R] === 1;
};

const inDistrict = (map: MapDef, d: DistrictDef, q: number, r: number): boolean =>
  hexDist(districtCenter(map.bastion, d.side), { q, r }) < DISTRICT.radius;

/** Inside the walls (keep and plaza included). Only bastions grow districts. */
export const isInside = (map: MapDef, q: number, r: number): boolean => {
  // Terraced land: inside the walls the group raised, as the wave found them (`openFissures`).
  if (map.land) return landInside(map, q, r);
  if (hexLen(q, r) < map.bastion) return true;
  for (const d of map.districts ?? []) if (inDistrict(map, d, q, r)) return true;
  return false;
};

/** Which district a cell inside belongs to (the first one), or undefined for the old bastion. */
export const districtAt = (map: MapDef, q: number, r: number): DistrictDef | undefined => {
  if (hexLen(q, r) < map.bastion) return undefined;
  return (map.districts ?? []).find((d) => inDistrict(map, d, q, r));
};

/** On the wall: just outside the walled ground. */
export const isWall = (map: MapDef, q: number, r: number): boolean => {
  if (hexLen(q, r) > map.radius || isInside(map, q, r)) return false;
  for (const d of DIRS) if (isInside(map, q + d.q, r + d.r)) return true;
  return false;
};

/** Every wall cell, row by row (deterministic). */
export const wallCells = (map: MapDef): Hex[] => {
  const out: Hex[] = [];
  const R = map.radius;
  for (let r = -R; r <= R; r++) for (let q = -R; q <= R; q++) if (isWall(map, q, r)) out.push({ q, r });
  return out;
};

/** The cell just inside a wall cell, nearest the keep. */
export const innerStep = (map: MapDef, q: number, r: number): Hex => {
  let best: Hex = { q, r };
  let bd = 0x7fffffff;
  for (const d of DIRS) {
    const n = { q: q + d.q, r: r + d.r };
    const l = hexLen(n.q, n.r);
    if (isInside(map, n.q, n.r) && l < bd) {
      bd = l;
      best = n;
    }
  }
  return best;
};

/**
 * Entrances once the walls have moved: walking each path in from its spawn, the gate is the first cell
 * on the wall, and the path stops just before it. Cells the walls took in become ground inside.
 */
export const reroute = (map: MapDef, sectors: SectorDef[]): SectorDef[] | null => {
  const out: SectorDef[] = [];
  for (const sec of sectors) {
    const full = [...sec.path, sec.gate];
    const i = full.findIndex((h) => isWall(map, h.q, h.r) || isInside(map, h.q, h.r));
    // The walls must leave some road outside every entrance (and never swallow where nightmares come from).
    if (i < 2 || !isWall(map, (full[i] as Hex).q, (full[i] as Hex).r)) return null;
    out.push({ index: sec.index, gate: full[i] as Hex, path: full.slice(0, i) });
  }
  return out;
};

/** The map with one more district, or null when that side can't take one. */
export const withDistrict = (map: MapDef, d: DistrictDef): MapDef | null => {
  if (map.kind !== 'bastion' || d.side < 0 || d.side >= DISTRICT.sides || (map.districts ?? []).some((x) => x.side === d.side)) return null;
  const next: MapDef = { ...map, districts: [...(map.districts ?? []), d] };
  // The district and its wall must fit on the land, with room left around it.
  const c = districtCenter(map.bastion, d.side);
  if (hexLen(c.q, c.r) + DISTRICT.radius + 2 > map.radius) return null;
  const sectors = reroute(next, map.sectors);
  if (!sectors) return null;
  next.sectors = sectors;
  return next;
};

/** The era of a bastion map: I with no districts, II once the first is raised, III from the third, then one more per crowning. */
export const mapEra = (map: MapDef): number => {
  const n = map.districts?.length ?? 0;
  return 1 + idiv(n + DISTRICTS_PER_ERA - 1, DISTRICTS_PER_ERA) + (map.crowns ?? 0);
};
